import { ActionTree } from 'vuex';
import { ContentLinkBlockService } from '@/api/content-link/services/content-link-block.service';
import { YoutubeService } from '@/api/video/youtube/services/youtube.service';
import { IYoutubeDataDTO } from '@/api/video/youtube/types/youtube-data.dto';
import { ContentLinkHelper } from '@/content-link/helpers/content-link.helper';
import {
  EContentLinkAction,
  EContentLinkMutation,
  state as defaultState,
} from '@/content-link/store/content-link.constants';
import { IContentLinkBlockState } from '@/content-link/store/content-link.types';
import { IContentLinkBlock } from '@/content-link/types/content-link';
import { translate } from '@/helpers/translation.helper';
import { RootState } from '@/store/store';
import { VideoLinkHelper } from '@/video/link/helpers/video-link.helper';
import { EVideoSource } from '@/video/types/enums/video-source';
import { DealroomHelper } from '@/dealroom/helpers/dealroom.helper';

export const actions: ActionTree<IContentLinkBlockState, RootState> = {
  async [EContentLinkAction.Preview]({ commit, dispatch, state }) {
    try {
      commit(EContentLinkMutation.SetLoading, true);
      commit(EContentLinkMutation.SetLoaded, false);

      let contentLinkBlockPreview: Partial<IContentLinkBlock>;

      // If Youtube URL then get Youtube data instead of using backend scraper
      if (VideoLinkHelper.getVideoSource(state.preview.url) === EVideoSource.Youtube) {
        const youtubeData: IYoutubeDataDTO = await YoutubeService.getYoutubeData(state.preview.url);
        contentLinkBlockPreview = ContentLinkHelper.convertYoutubeDataToContentLink(youtubeData);
      } else {
        contentLinkBlockPreview = await ContentLinkBlockService.loadPreview(state.preview.url);
      }
      dispatch(EContentLinkAction.PreviewSuccess, contentLinkBlockPreview);
    } catch (error) {
      if (error.networkError) {
        dispatch(EContentLinkAction.PreviewFailed);
      } else {
        dispatch(EContentLinkAction.PreviewScrapeFailed, state.preview.url);
      }
    }
  },
  [EContentLinkAction.PreviewSuccess]({ commit }, contentLinkBlock: IContentLinkBlock) {
    commit(EContentLinkMutation.SetLoading, false);
    commit(EContentLinkMutation.SetLoaded, true);
    commit(EContentLinkMutation.SetPreview, contentLinkBlock);
  },
  [EContentLinkAction.PreviewFailed]({ commit }) {
    commit(EContentLinkMutation.SetLoading, false);
    commit(EContentLinkMutation.SetMessage, 'could-not-preview-url');
  },
  [EContentLinkAction.PreviewScrapeFailed]({ commit, state }, contentLinkUrl: string) {
    commit(EContentLinkMutation.SetLoading, false);
    commit(
      EContentLinkMutation.SetMessage,
      'could-not-get-data-from-url-but-you-can-still-create-link-block'
    );
    const emptyContentLink: IContentLinkBlock = {
      title: '',
      description: '',
      url: '',
      buttonText: '',
      thumbUrl: '',
    };
    commit(EContentLinkMutation.SetPreview, {
      ...emptyContentLink,
      url: contentLinkUrl,
      buttonText: state.preview.buttonText,
    });
  },
  [EContentLinkAction.ResetState]({ commit }) {
    commit(EContentLinkMutation.ClearData, { ...defaultState });
  },
  [EContentLinkAction.Update]({ commit, state }, payload: Partial<IContentLinkBlock>) {
    const updatedContentLink: IContentLinkBlock = { ...state.preview, ...payload };
    commit(EContentLinkMutation.SetPreview, updatedContentLink);
  },
  async [EContentLinkAction.SaveEdit](
    { commit, dispatch },
    { contentLinkBlock, file }: { contentLinkBlock: IContentLinkBlock; file: File }
  ) {
    commit(EContentLinkMutation.SetSaving, true);
    const payload: IContentLinkBlock = ContentLinkHelper.getPayload(contentLinkBlock, file);
    try {
      const createdContentLink: IContentLinkBlock = await ContentLinkBlockService.update(payload);
      if (file) {
        const uploadLink: string = await ContentLinkBlockService.getUploadUrl(
          createdContentLink.id
        );
        await ContentLinkBlockService.uploadThumbnail({ file, uploadLink });
      }
      dispatch(EContentLinkAction.CreateSuccess, createdContentLink);
    } catch (e) {
      dispatch(EContentLinkAction.CreateFailed);
    }
  },
  [EContentLinkAction.SetURLValidation]({ commit, dispatch, state }, valid?: boolean) {
    commit(EContentLinkMutation.SetURLValidation, valid);
    commit(EContentLinkMutation.SetMessage, undefined);
    if (valid) {
      dispatch(EContentLinkAction.Preview);
      return;
    }
    if (valid === false) {
      commit(EContentLinkMutation.SetMessage, 'url-is-invalid');
    }
    commit(EContentLinkMutation.SetLoaded, false);
    commit(EContentLinkMutation.SetPreview, {
      ...defaultState.preview,
      url: state.preview.url,
      buttonText: state.preview.buttonText,
    });
  },
  [EContentLinkAction.SetPreview]({ commit }, contentLinkBlock: IContentLinkBlock) {
    commit(EContentLinkMutation.SetPreview, contentLinkBlock);
    commit(EContentLinkMutation.SetLoaded, true);
  },
  async [EContentLinkAction.Create](
    { dispatch, commit },
    { contentLinkBlock, file }: { contentLinkBlock: IContentLinkBlock; file: File }
  ) {
    commit(EContentLinkMutation.SetSaving, true);
    try {
      const payload: IContentLinkBlock = ContentLinkHelper.getPayload(contentLinkBlock, file);
      const createdContentLink: IContentLinkBlock = await ContentLinkBlockService.create(payload);

      if (file) {
        const uploadLink: string = await ContentLinkBlockService.getUploadUrl(
          createdContentLink.id
        );
        await ContentLinkBlockService.uploadThumbnail({ file, uploadLink });
      }
      dispatch(EContentLinkAction.CreateSuccess, createdContentLink);
    } catch (error) {
      dispatch(EContentLinkAction.CreateFailed);
    }
  },
  [EContentLinkAction.CreateSuccess]({ commit }, contentLink: IContentLinkBlock) {
    commit(EContentLinkMutation.SetPreview, contentLink);
    commit(EContentLinkMutation.SetIsCreated, true);
  },
  [EContentLinkAction.CreateFailed]({ dispatch, commit }) {
    dispatch('alert/showDangerAlert', translate('failed-to-add-link'), {
      root: true,
    });
    commit(EContentLinkMutation.SetSaving, false);
  },
  [EContentLinkAction.SetExistingData]({ commit, dispatch }, contentLinkBlock: IContentLinkBlock) {
    dispatch(EContentLinkAction.SetPreview, contentLinkBlock);
    const validURL: boolean = DealroomHelper.isValidUrl(contentLinkBlock.url);
    commit(EContentLinkMutation.SetURLValidation, validURL);
  },
};
